/**
 * ToggleChipPopover
 * Reusable multi-select filter popover with toggle chips + APPLY/Reset.
 * Handles local state, sync-on-open, and deferred-commit pattern.
 * Used for: Intention, Spiritual Traditions, Format, Duration filters.
 */

import { useCallback, useState } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { FilterChipButton } from "./FilterChipButton"
import { PopoverFooter } from "./PopoverFooter"
import { ToggleChip } from "./ToggleChip"

interface ToggleChipOption {
  value: string
  label: string
}

interface ToggleChipPopoverProps {
  /** Label shown on the trigger chip (e.g. "Intention", "Format") */
  label: string
  /** Options to display as toggle chips */
  options: ToggleChipOption[]
  /** Currently committed selected values */
  selected: string[]
  /** Called when user clicks APPLY with the new selection */
  onApply: (values: string[]) => void
  /** Optional: render extra content above the chips (e.g. price slider) */
  extraContent?: (
    local: string[],
    setLocal: React.Dispatch<React.SetStateAction<string[]>>,
  ) => React.ReactNode
  /** Max width for the popover content */
  maxWidth?: string
}

export function ToggleChipPopover({
  label,
  options,
  selected,
  onApply,
  extraContent,
  maxWidth = "550px",
}: ToggleChipPopoverProps) {
  const [open, setOpen] = useState(false)
  const [local, setLocal] = useState<string[]>(selected)

  const handleOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) setLocal(selected)
      setOpen(nextOpen)
    },
    [selected],
  )

  const toggleValue = useCallback((value: string) => {
    setLocal(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]))
  }, [])

  const hasSelections = selected.length > 0
  const displayValue = hasSelections ? String(selected.length) : undefined

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <FilterChipButton
          label={`${label}${displayValue ? ":" : ""}`}
          value={displayValue}
          isActive={open || hasSelections}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={10}
        className={`w-auto bg-popover border border-border rounded-[20px] p-[20px]`}
        style={{ maxWidth }}
      >
        <div className="flex flex-col gap-[20px]">
          <div className="flex flex-wrap items-center gap-[10px]">
            {options.map(option => (
              <ToggleChip
                key={option.value}
                label={option.label}
                selected={local.includes(option.value)}
                onToggle={() => toggleValue(option.value)}
              />
            ))}
          </div>
          {extraContent?.(local, setLocal)}
          <PopoverFooter
            onApply={() => {
              onApply(local)
              setOpen(false)
            }}
            onReset={() => setLocal([])}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
